import React from "react";
import {
  Box,
  Flex,
  Grid,
  Image,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import StatusBadge from "@/shared/components/StatusBadge";
import PathPickAiImageBadge from "@/features/pathpick/components/product/PathPickAiImageBadge";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  ORDER_DETAIL_KEY,
  ORDER_DETAIL_OPTION_COLUMNS,
  ORDER_DETAIL_OPTION_TEMPLATE_COLUMNS,
} from "@/features/pathpick/features/order-history/features/detail/constants";
import {
  PathPickOrder,
  PathPickOrderItem,
} from "@/features/pathpick/features/order-history/types/response";
import {
  getPathPickOrderItemImageUrl,
  isPathPickOrderItemImageAiCollected,
} from "@/features/pathpick/features/order-history/utils";
import {
  getPathPickItemSubtotal,
  getPathPickOptionLineTotal,
  isPathPickOrderItemCancelDetermined,
  isPathPickOrderItemQuantityConfirmed,
  resolvePathPickCancelReasonKey,
} from "@/features/pathpick/features/order-history/features/detail/utils";

interface Props {
  item: PathPickOrderItem;
  // 상품 단위 플래그의 폴백(과거 주문·주문 전체 취소) 때문에 주문도 함께 받는다.
  order: PathPickOrder;
}

function PathPickOrderDetailProductRow({ item, order }: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  // 확정 수량이 non-nullable이라 "확인 중" 표기는 플래그로만 판단할 수 있는데,
  // 수량 확인 중에도 상품은 개별 확정되므로 주문이 아니라 상품 단위로 정한다.
  const isQuantityConfirmed = isPathPickOrderItemQuantityConfirmed(order, item);
  // 취소 수량은 주문 취소로도 결정되므로 확정 여부와 별개의 플래그를 쓴다.
  const isCancelDetermined = isPathPickOrderItemCancelDetermined(order, item);
  const checking = t(`${ORDER_DETAIL_KEY}qty_checking`);
  const price = (value: number) =>
    tCurrencyString(value, { type: "SYMBOL", code: item.currency });
  // 7열 표는 모바일 폭을 넘어 가로 스크롤이 생긴다 — 뷰포트 폭 문제라 UA가 아니라 브레이크포인트로 가른다.
  const isNarrow = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  // 표와 카드가 같은 표기·색 규칙을 쓰도록 옵션별 표시 값을 한 곳에서 만든다.
  const optionViews = item.options.map((option) => {
    const cancelReasonKey = resolvePathPickCancelReasonKey(option.cancelReason);

    return {
      option,
      cancelReasonLabel: cancelReasonKey
        ? t(cancelReasonKey)
        : option.cancelReasonNote ?? option.cancelReason,
      canceledLabel: isCancelDetermined ? option.canceledQuantity : checking,
      canceledColor:
        isCancelDetermined && option.canceledQuantity > 0
          ? "#EE7D7A"
          : "#444440",
      confirmedLabel: isQuantityConfirmed ? option.confirmedQuantity : checking,
      lineTotal: price(
        getPathPickOptionLineTotal(option, item.salePrice, isQuantityConfirmed)
      ),
    };
  });
  // 상품 합계 — 행 합계의 총합이라 표시 값끼리 어긋나지 않는다
  const subtotal = price(getPathPickItemSubtotal(item, isQuantityConfirmed));

  const renderMetric = (
    labelKey: string,
    value: React.ReactNode,
    color = "#444440"
  ) => (
    <Flex alignItems={"baseline"} gap={"4px"}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {t(labelKey)}
      </Text>
      <Text textStyle={"body-sm-bold"} color={color}>
        {value}
      </Text>
    </Flex>
  );

  return (
    <Flex
      gap={"16px"}
      alignItems={"flex-start"}
      flexDirection={{ base: "column", md: "row" }}
    >
      {/* 상품 정보 — 좁은 폭에서는 220px 고정을 풀어야 아래 옵션표가 눌리지 않는다 */}
      <Flex
        gap={"12px"}
        width={{ base: "100%", md: "220px" }}
        flexShrink={{ base: 1, md: 0 }}
      >
        <Box position={"relative"} flexShrink={0}>
          <Image
            src={getPathPickOrderItemImageUrl(item) ?? ""}
            fallbackSrc={"/icons-v1_1/null.png"}
            boxSize={"64px"}
            objectFit={"contain"}
            borderRadius={"8px"}
            border={"0.5px solid #EFEFED"}
            background={"#FFFFFF"}
            alt={item.productName ?? item.sku}
          />
          {isPathPickOrderItemImageAiCollected(item) && (
            <PathPickAiImageBadge size={"small"} />
          )}
        </Box>
        <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
          <Flex gap={"4px"} alignItems={"center"}>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {item.brandName}
            </Text>
            <StatusBadge
              status={t(
                item.isHotdeal
                  ? `${ORDER_DETAIL_KEY}badge_hotdeal`
                  : `${ORDER_DETAIL_KEY}badge_normal`
              )}
              colorVariant={item.isHotdeal ? "yellow" : "gray"}
              size={"small"}
            />
          </Flex>
          {/* 라인시트 상품은 상품명이 없을 수 있어 SKU로 폴백한다 */}
          <Text
            textStyle={"body-sm-bold"}
            color={"#1D1D1B"}
            noOfLines={2}
            wordBreak={"break-all"}
          >
            {item.productName ?? item.sku}
          </Text>
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {item.sku}
          </Text>
          {item.color && (
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {item.color}
            </Text>
          )}
        </Flex>
      </Flex>

      {isNarrow ? (
        // 모바일 — 옵션 1개를 세로로 읽는 블록으로. 단가는 상품 단위 값이라 맨 위에 한 번만 둔다.
        <Flex
          width={"100%"}
          flexDirection={"column"}
          border={"0.5px solid #EFEFED"}
          borderRadius={"8px"}
          overflow={"hidden"}
        >
          <Flex padding={"10px 12px"} background={"#F7F9FA"}>
            {renderMetric(
              `${ORDER_DETAIL_KEY}col_unit_price`,
              price(item.salePrice)
            )}
          </Flex>
          {optionViews.map((view) => (
            <Flex
              key={view.option.id}
              flexDirection={"column"}
              gap={"6px"}
              padding={"12px"}
              borderTop={"0.5px solid #EFEFED"}
            >
              <Flex
                justifyContent={"space-between"}
                alignItems={"baseline"}
                gap={"8px"}
              >
                <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                  {view.option.displayName || view.option.name}
                </Text>
                <Text
                  textStyle={"body-sm-bold"}
                  color={"#1D1D1B"}
                  flexShrink={0}
                >
                  {view.lineTotal}
                </Text>
              </Flex>
              <Flex flexWrap={"wrap"} columnGap={"12px"} rowGap={"4px"}>
                {renderMetric(
                  `${ORDER_DETAIL_KEY}col_requested`,
                  view.option.orderedQuantity
                )}
                {renderMetric(
                  `${ORDER_DETAIL_KEY}col_canceled`,
                  view.canceledLabel,
                  view.canceledColor
                )}
                {renderMetric(
                  `${ORDER_DETAIL_KEY}col_confirmed`,
                  view.confirmedLabel,
                  "#1D1D1B"
                )}
              </Flex>
              {view.cancelReasonLabel &&
                renderMetric(
                  `${ORDER_DETAIL_KEY}col_cancel_reason`,
                  view.cancelReasonLabel,
                  "#EE7D7A"
                )}
            </Flex>
          ))}
          <Flex
            justifyContent={"space-between"}
            alignItems={"baseline"}
            gap={"8px"}
            padding={"12px"}
            background={"#F7F9FA"}
            borderTop={"0.5px solid #EFEFED"}
          >
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t(`${ORDER_DETAIL_KEY}subtotal`)}
            </Text>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {subtotal}
            </Text>
          </Flex>
        </Flex>
      ) : (
        // 옵션 테이블 — 좁아지면 카드 내부에서만 가로 스크롤
        <Flex
          flex={1}
          minW={0}
          flexDirection={"column"}
          border={"0.5px solid #EFEFED"}
          borderRadius={"8px"}
          overflowX={"auto"}
        >
          {/* 행들을 표 내용 폭까지 넓힌다 — 없으면 각 행 박스가 스크롤 컨테이너 폭에 머물러
              가로 스크롤 시 셀 글자만 넘어오고 행 배경·구분선은 처음 보이던 폭에서 끊긴다.
              행마다 따로 넓히면 1fr 트랙 폭이 행별로 달라져 열이 어긋나므로 한 래퍼로 묶는다. */}
          <Flex flexDirection={"column"} minW={"max-content"}>
            <Grid
              templateColumns={ORDER_DETAIL_OPTION_TEMPLATE_COLUMNS}
              gap={"12px"}
              padding={"10px 16px"}
              background={"#F7F9FA"}
            >
              {ORDER_DETAIL_OPTION_COLUMNS.map((column) => (
                <Text
                  key={column.labelKey}
                  textStyle={"caption-medium"}
                  color={"#8F8F8C"}
                  textAlign={column.textAlign}
                >
                  {t(column.labelKey)}
                </Text>
              ))}
            </Grid>
            {optionViews.map((view, index) => (
              <Grid
                key={view.option.id}
                templateColumns={ORDER_DETAIL_OPTION_TEMPLATE_COLUMNS}
                gap={"12px"}
                alignItems={"center"}
                padding={"12px 16px"}
                borderTop={index > 0 ? "0.5px solid #EFEFED" : undefined}
              >
                <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                  {view.option.displayName || view.option.name}
                </Text>
                <Text
                  textStyle={"body-sm-medium"}
                  color={"#444440"}
                  textAlign={"right"}
                >
                  {price(item.salePrice)}
                </Text>
                <Text
                  textStyle={"body-sm-medium"}
                  color={"#444440"}
                  textAlign={"center"}
                >
                  {view.option.orderedQuantity}
                </Text>
                <Text
                  textStyle={"body-sm-bold"}
                  color={view.canceledColor}
                  textAlign={"center"}
                >
                  {view.canceledLabel}
                </Text>
                <Text
                  textStyle={"body-sm-medium"}
                  color={view.cancelReasonLabel ? "#EE7D7A" : "#8F8F8C"}
                >
                  {view.cancelReasonLabel || "-"}
                </Text>
                <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                  {view.confirmedLabel}
                </Text>
                <Text
                  textStyle={"body-sm-bold"}
                  color={"#1D1D1B"}
                  textAlign={"right"}
                >
                  {view.lineTotal}
                </Text>
              </Grid>
            ))}
            <Grid
              templateColumns={ORDER_DETAIL_OPTION_TEMPLATE_COLUMNS}
              gap={"12px"}
              alignItems={"center"}
              padding={"12px 16px"}
              background={"#F7F9FA"}
              borderTop={"0.5px solid #EFEFED"}
            >
              <Box gridColumn={"1 / 6"} />
              <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {t(`${ORDER_DETAIL_KEY}subtotal`)}
              </Text>
              <Text
                textStyle={"body-sm-bold"}
                color={"#1D1D1B"}
                textAlign={"right"}
              >
                {subtotal}
              </Text>
            </Grid>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

export default PathPickOrderDetailProductRow;
